import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const NewsBlogs = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const cards = [
    {
      bgColor: "bg-[#CEF5A4]",
      heading: "Complete Offerings",
      title: "Financial Consulting Insights That Drive Growth",
      description: "We Specialize In Finance And Business Consulting.",
    },
    {
      bgColor: "bg-[#F6F9F1]",
      heading: "Complete Offerings",
      title: "Why Financial Consulting Matters More Than Ever",
      description: "We Specialize In Finance And Business Consulting.",
    },
    {
      bgColor: "bg-[#001D21] text-white",
      heading: "Complete Offerings",
      title: "Financial Consulting Industry on the Rise",
      description:
        "Our Financial Consulting Services Include Strategy And Optimization.",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const titleVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const dividerVariants = {
    hidden: { width: 0, opacity: 0 },
    visible: {
      width: "100%",
      opacity: 1,
      transition: {
        duration: 0.6,
        delay: 0.4,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.8,
        duration: 0.5,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      id="news"
      ref={sectionRef}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className="w-full pb-8 px-4 sm:px-6 lg:px-12 overflow-hidden mt-8"
    >
      {/* Section Title */}
      <motion.h2
        style={{ lineHeight: "1.2" }}
        className="text-3xl md:text-4xl font-bold text-primaryBgColor mb-12 text-center"
        variants={titleVariants}
      >
        TechSpear Updated News & Blogs
      </motion.h2>

      {/* Card Grid */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        variants={containerVariants}
      >
        {cards.map((card, index) => (
          <motion.div
            key={index}
            variants={cardVariants}
            whileHover="hover"
            className={`${card.bgColor} p-8 rounded-lg shadow-lg hover:shadow-xl transition-all overflow-hidden relative`}
          >
            {/* Animated background pattern */}
            <motion.div
              className="absolute top-0 right-0 w-32 h-32 opacity-5"
              initial={{ rotate: 0 }}
              whileHover={{
                rotate: 10,
                transition: { duration: 0.8 },
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path
                  d="M0,0 L100,100 M20,0 L100,80 M0,20 L80,100"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </motion.div>

            {/* Heading */}
            <motion.p
              className="text-sm mb-20 opacity-80 tracking-wide relative z-10"
              variants={contentVariants}
            >
              {card.heading}
            </motion.p>

            {/* Main Title */}
            <motion.h3
              className="text-xl md:text-2xl tracking-wide font-semibold mb-6 relative z-10"
              variants={contentVariants}
            >
              {card.title}
            </motion.h3>

            {/* Divider */}
            <motion.hr
              className="border-gray-400 mb-4 relative z-10"
              variants={dividerVariants}
            />

            {/* Description */}
            <motion.p
              className="text-sm md:text-base tracking-wide opacity-80 relative z-10"
              variants={contentVariants}
            >
              {card.description}
            </motion.p>

            {/* Read more indicator (appears on hover) */}
            <motion.div
              className="absolute bottom-6 right-6 flex items-center"
              initial={{ opacity: 0, x: 10 }}
              whileHover={{
                opacity: 1,
                x: 0,
                transition: { duration: 0.3 },
              }}
            >
              <span className="text-xs mr-2">Read more</span>
              <motion.svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                animate={{ x: [0, 5, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  );
};

export default NewsBlogs;
